import { recommendedSahConsentsMissing } from '@statura/utils/sah-consent';
import type { AssessmentSummaryView, SahPanelModel } from '@/components/clients/useSahBudgetModel';
import { type ClientCarePlanReturnContext } from '@/lib/sah/client-care-plan-navigation';
import {
  resolveSahCarePlanDestination,
  type SahCarePlanDestinationIntent,
} from '@/lib/sah/sah-care-plan-destination';
import { isSahSupportPlanStatus, supportPlanStatusRank } from '@/lib/sah/support-plan-status-rank';

/**
 * SAH care-plan start recommendations.
 *
 * Grounded in:
 * - Support at Home Program Manual (participant-centred care plan, services aligned
 *   to assessed needs and approved budget)
 * - ACQSC Quality Standards (Std 2 partner in care; Std 3 personal/care services;
 *   Std 4 services and supports)
 * - Provider operating patterns observed in market (AlayaCare, LeeCare, AutumnCare):
 *   assessment-led synthesis, conversation capture, budget-first for transitions,
 *   template-led for scaled coordinators, clone for plan reviews.
 *
 * Personas:
 * - Intake coordinator: assessment complete → synthesis or conversation
 * - Clinical lead / care manager: home-visit conversation capture
 * - Finance / transition officer: HCP opening + quarter budget before services
 * - Brokered provider: authority/consents first (Journey phases before plan start)
 * - Self-managed participant: plan acknowledgement after provider authoring (portal)
 */

export type SahCarePlanStartEntryKey =
  | 'conversation'
  | 'assessment_synthesis'
  | 'budget'
  | 'template'
  | 'scratch'
  | 'clone';

export type SahCarePlanStartRecommendation = {
  key: SahCarePlanStartEntryKey;
  label: string;
  detail: string;
  rationale: string;
  highlighted: boolean;
};

export type SahCarePlanPersona = 'intake' | 'clinical' | 'finance' | 'brokered' | 'self_managed';

export type SahCarePlanNavigationOptions = {
  /** When false, non-draft SAH plans route to monolith anchors instead of compose. */
  composeDefaultRedirect?: boolean;
  intent?: SahCarePlanDestinationIntent;
};

export type SahCarePlanPersonaGuidance = {
  persona: SahCarePlanPersona;
  title: string;
  detail: string;
};

const SAH_LIFECYCLE_ASSESSMENT_TYPES = new Set([
  'ACAT',
  'COMPREHENSIVE_CARE',
  'FUNCTIONAL',
  'IAT',
  'INITIAL',
  'NSAF',
  'REASSESSMENT',
  'REVIEW',
  'SUPPORT_NEEDS',
]);

function isCompletedAssessment(assessment: AssessmentSummaryView): boolean {
  return (
    ['COMPLETED', 'SIGNED_OFF'].includes(String(assessment.status).toUpperCase()) ||
    Boolean(assessment.completed_at)
  );
}

function isSahLifecycleAssessment(assessment: AssessmentSummaryView): boolean {
  const assessmentType = String(assessment.assessment_type || '').toUpperCase();
  return SAH_LIFECYCLE_ASSESSMENT_TYPES.has(assessmentType);
}

export function currentSahSupportPlan(data: SahPanelModel) {
  return (
    [...data.carePlans]
      .filter((plan) => plan.plan_type === 'SUPPORT_AT_HOME' && isSahSupportPlanStatus(plan.status))
      .sort((left, right) => {
        const statusDelta =
          supportPlanStatusRank(left.status) - supportPlanStatusRank(right.status);
        if (statusDelta !== 0) return statusDelta;
        return Number(right.version ?? 1) - Number(left.version ?? 1);
      })[0] ?? null
  );
}

export function hasSahLifecycleAssessmentEvidence(data: SahPanelModel): boolean {
  return data.assessments.some(
    (assessment) => isCompletedAssessment(assessment) && isSahLifecycleAssessment(assessment),
  );
}

export function sahCarePlanReturnContext(clientId: string): ClientCarePlanReturnContext {
  return { clientId, returnTab: 'sah-hub' };
}

/** Anchor / ?panel= the Journey start fold and SahCarePlanStartPanel share. */
export const SAH_CARE_PLAN_START_HASH = 'care-plan-start';

export function sahCarePlanStartHref(clientId: string): string {
  return resolveSahCarePlanDestination({ clientId, plan: null }).href;
}

export function resolveSahCarePlanPersona(data: SahPanelModel): SahCarePlanPersona {
  const missingAuthority =
    recommendedSahConsentsMissing(data.consents).length > 0 || data.decisionMakers.length === 0;
  if (missingAuthority) return 'brokered';
  if (data.isTransitioner && !data.currentBudget) return 'finance';
  if (data.capacity === 'SELF_MANAGED') return 'self_managed';
  if (hasSahLifecycleAssessmentEvidence(data)) return 'clinical';
  return 'intake';
}

export function sahCarePlanPersonaGuidance(
  persona: SahCarePlanPersona,
): Omit<SahCarePlanPersonaGuidance, 'persona'> {
  switch (persona) {
    case 'brokered':
      return {
        title: 'Brokered provider path',
        detail:
          'Complete authority and recommended consents on the Journey hub before locking in services. Plan authoring stays available but is not enforced.',
      };
    case 'finance':
      return {
        title: 'Finance-led path',
        detail:
          'Provision the quarter budget and opening balance before finalising affordable services.',
      };
    case 'clinical':
      return {
        title: 'Clinical assessment path',
        detail: 'Use completed assessment evidence to synthesise the draft plan without re-keying.',
      };
    case 'self_managed':
      return {
        title: 'Self-managed participant path',
        detail:
          'Author the provider plan here, then route acknowledgement and partner-in-care steps through the portal.',
      };
    default:
      return {
        title: 'Intake coordinator path',
        detail: 'Start with conversation capture or assessments once intake evidence is available.',
      };
  }
}

export function resolveSahCarePlanPersonaGuidance(data: SahPanelModel): SahCarePlanPersonaGuidance {
  const persona = resolveSahCarePlanPersona(data);
  return { persona, ...sahCarePlanPersonaGuidance(persona) };
}

/** Compose-first SAH plan workspace href for Journey lifecycle and continue CTAs. */
export function resolveSahCarePlanWorkspaceHref(
  data: SahPanelModel,
  clientId: string,
  planId?: string | null,
  options?: SahCarePlanNavigationOptions,
): string {
  const plan =
    planId != null
      ? (data.carePlans.find((row) => row.id === planId) ?? null)
      : currentSahSupportPlan(data);
  return resolveSahCarePlanDestination({
    clientId,
    plan,
    intent: options?.intent ?? 'continue',
    returnContext: sahCarePlanReturnContext(clientId),
    hasCurrentServiceInclusions: data.hasCurrentServiceInclusions,
    composeDefaultRedirect: options?.composeDefaultRedirect,
  }).href;
}

export function resolveSahCarePlanContinueHref(
  data: SahPanelModel,
  clientId: string,
  options?: SahCarePlanNavigationOptions,
): string | null {
  const plan = currentSahSupportPlan(data);
  if (!plan) return null;
  return resolveSahCarePlanWorkspaceHref(data, clientId, plan.id, options);
}

export function recommendSahCarePlanStarts(data: SahPanelModel): SahCarePlanStartRecommendation[] {
  const hasAssessment = hasSahLifecycleAssessmentEvidence(data);
  const persona = resolveSahCarePlanPersona(data);
  const needsBudgetFirst = persona === 'finance';
  const needsAuthorityFirst = persona === 'brokered';

  const entries: SahCarePlanStartRecommendation[] = [
    {
      key: 'conversation',
      label: 'Conversation',
      detail: 'Capture an in-person conversation and turn agreed items into the plan.',
      rationale:
        persona === 'self_managed'
          ? 'Author the provider plan here, then route acknowledgement through the client portal.'
          : 'Participant-centred default aligned to ACQSC Std 2 and Support at Home care-plan expectations.',
      highlighted:
        !needsAuthorityFirst &&
        !needsBudgetFirst &&
        (persona === 'intake' || persona === 'self_managed') &&
        !hasAssessment,
    },
    {
      key: 'assessment_synthesis',
      label: 'Assessments',
      detail: 'Synthesize completed assessments into one draft plan.',
      rationale: hasAssessment
        ? 'Assessment evidence is available — synthesis avoids re-keying intake findings.'
        : 'Use once provider intake assessments are completed.',
      highlighted:
        !needsAuthorityFirst && !needsBudgetFirst && persona === 'clinical' && hasAssessment,
    },
    {
      key: 'budget',
      label: 'Budget',
      detail: 'Start with the participant budget and add affordable services.',
      rationale: needsBudgetFirst
        ? 'HCP transition clients need quarter budget and opening balance before service mix.'
        : 'Finance-led providers can anchor services to the approved envelope.',
      highlighted: needsBudgetFirst,
    },
    {
      key: 'template',
      label: 'Template',
      detail: 'Start from the provider care-plan template.',
      rationale: 'Scaled providers with standardised SAH service bundles.',
      highlighted: false,
    },
    {
      key: 'scratch',
      label: 'Scratch',
      detail: 'Start a blank guided draft.',
      rationale: 'Experienced coordinators authoring from prior provider notes.',
      highlighted: false,
    },
  ];

  const highlighted = entries.find((entry) => entry.highlighted);
  const firstEntry = entries[0];
  if (!highlighted && firstEntry) {
    entries[0] = { ...firstEntry, highlighted: true };
  }
  return entries;
}

export function sahCarePlanStartAnchorHref(clientId: string): string {
  return sahCarePlanStartHref(clientId);
}
